import { useTranslation } from 'react-i18next';
import React, { Suspense, useState } from 'react';
import { FlexBox, ModalNew, Tooltip, message, Typography, Box } from '@helloworld1812/ws-components';
import { useUsersMeCached, WsResponseError } from '@helloworld1812/hr-api';
import { useFeatureIsOn } from '@helloworld1812/ws-growthbook';
import { Loader, LanguageSwitcher } from '@helloworld1812/app-global-components';
import { UserPasswordForm, UserPasswordFormValues } from '@helloworld1812/auth-routes';
import { wsLocalStorage } from '@helloworld1812/ws-storage';
import { IconInfo, IconPencil } from '@helloworld1812/ws-icons';
import { AxiosError } from 'axios';

import { useHrMutation } from '@/api';

import { UserPhoneEditModal } from './user-phone-edit-modal';

export const MyProfilePage = () => {
  const { t } = useTranslation('hr-account-routes');

  const { user, refetch } = useUsersMeCached();

  const isFrenchSupportOn = useFeatureIsOn('french-support');
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [isPhoneNumberModalVisible, setIsPhoneNumberModalVisible] = useState(false);

  const { mutate: updatePassword, isLoading: isPasswordUpdating } = useHrMutation(
    {
      url: '/v1/users/update_password',
      method: 'put',
    },
    {
      onSuccess: () => {
        setPasswordModalVisible(false);
        message.success(t('Password changed successfully'));
      },
      onError: (e: AxiosError<WsResponseError>) => {
        message.error(e?.response?.data?.message || t('Failed to update password'));
      },
    }
  );

  const handlePasswordSubmit = async (values: UserPasswordFormValues) => {
    const { old_password, password } = values;
    updatePassword({
      current_password: old_password,
      password,
    });
  };

  const isSSO = wsLocalStorage.getWsIsSso();

  return (
    <div>
      <FlexBox column auto pr={2} px={32} py={16}>
        <Typography variant="h1" mb={48}>
          {user.name}
        </Typography>
        <FlexBox column gap={36}>
          <FlexBox gap={16}>
            <Typography variant="body" width={200}>
              {t('Email', { ns: 'common' })}
            </Typography>
            <Typography variant="body">{user.email}</Typography>
          </FlexBox>

          <FlexBox gap={16}>
            <FlexBox align="center" width={200} gap={8}>
              <Typography variant="body">{t('Phone number')}</Typography>
              <Tooltip title={t('Your phone number is only used for notifications and applicants will not see it.')}>
                <IconInfo size={20} />
              </Tooltip>
            </FlexBox>
            <FlexBox gap={8}>
              {user.region_number && user.phone ? (
                <Typography variant="body">
                  +{user.region_number} {user.phone}
                </Typography>
              ) : (
                <Typography variant="body">{t('No phone number yet.')}</Typography>
              )}
              <IconPencil size={20} onClick={() => setIsPhoneNumberModalVisible(true)} />
            </FlexBox>
          </FlexBox>

          {!isSSO && (
            <FlexBox gap={16}>
              <Typography variant="body" width={200}>
                {t('Password')}
              </Typography>
              <Typography
                data-testid="change_password_icon"
                variant="link"
                fontWeight={500}
                onClick={() => setPasswordModalVisible(true)}
              >
                {t('Change Password')}
              </Typography>
            </FlexBox>
          )}

          {isFrenchSupportOn && (
            <FlexBox gap={16}>
              <FlexBox align="center" width={200} gap={8}>
                <Typography variant="body">{t('Switch language')}</Typography>
              </FlexBox>
              <LanguageSwitcher />
            </FlexBox>
          )}
        </FlexBox>

        {passwordModalVisible && (
          <ModalNew
            variant="quick-action"
            headerBorder
            visible
            title={t('Change password')}
            footer={null}
            contentProps={{ pt: 32 }}
            onCancel={() => setPasswordModalVisible(false)}
            onClose={() => setPasswordModalVisible(false)}
            okButtonProps={{ loading: isPasswordUpdating }}
          >
            <Suspense
              fallback={
                <Box height={300}>
                  <Loader spinning />
                </Box>
              }
            >
              <UserPasswordForm onSubmit={handlePasswordSubmit} okCopy={t('Submit', { ns: 'common' })} oldPassword />
            </Suspense>
          </ModalNew>
        )}
        {isPhoneNumberModalVisible && (
          <UserPhoneEditModal
            onCancel={() => setIsPhoneNumberModalVisible(false)}
            onChanged={() => {
              refetch();
              setIsPhoneNumberModalVisible(false);
            }}
            regionNumber={user.region_number}
            phoneNumber={user.phone}
          />
        )}
      </FlexBox>
    </div>
  );
};
